import {
  Component,
  inject,
  ChangeDetectorRef,
  effect,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  ElementRef,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { CurrencyAmountDisplay } from 'src/app/shared/utils/currency/currency-format';
import { ThemePipe } from '../../../core/pipes/theme.pipe';
import { TabButtonComponent } from '../tab-button/tab-button.component';
import { PillTabButtonComponent } from '../pill-tab-button/pill-tab-button.component';
import { BalancePillComponent } from '../../pills/balance-pill/balance-pill.component';
import { NgClass, NgStyle } from '@angular/common';

@Component({
  selector: 'app-tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrls: ['./tab-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    PillTabButtonComponent,
    TabButtonComponent,
    ThemePipe,
    BalancePillComponent,
  ],
})
export class TabBarComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly tabs = input<{ label: string; id: string }[]>([]);
  readonly buttonType = input<'pill' | 'tab'>('pill');
  readonly selectedTabIndex = input(0);
  // The tab shown as selected: seeded from selectedTabIndex, moved by the bar itself on click
  // (a signal input is read-only), and reset whenever the parent's bound value changes.
  protected readonly activeTabIndex = linkedSignal(() => this.selectedTabIndex());
  readonly resultsTabId = input<string | null>(null);
  readonly pendingResultsCount = input<number | null>(null);
  readonly showPendingResultsDot = input(false);
  readonly balance = input<CurrencyAmountDisplay>();
  readonly tabSelected = output<string>();

  private changeDetection = inject(ChangeDetectorRef);
  private themeService = inject(ThemeService);
  private elementRef = inject(ElementRef);
  currentTheme = this.themeService.currentTheme;

  underlineTransform = 'translateX(0)';
  underlineWidth = '0px';
  circleRadius = '50px';
  shouldAnimate = false;

  private isDragging = false;
  private hasDragged = false;
  private dragStartX = 0;
  private scrollStartLeft = 0;
  private dragCleanup: (() => void)[] = [];

  constructor() {
    effect(() => {
      this.currentTheme();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const shouldUpdate = ['pendingResultsCount', 'selectedTabIndex'].some(
      (key) => changes[key] && !changes[key].firstChange,
    );

    if (shouldUpdate) {
      requestAnimationFrame(() => {
        this.updateUnderline();
        if (changes['selectedTabIndex']) {
          this.scrollToActiveTab();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.updateUnderline();
      this.scrollToActiveTab('instant');
      requestAnimationFrame(() => {
        this.shouldAnimate = true;
        this.changeDetection.markForCheck();
      });
    });
    this.initDragScroll();
  }

  ngOnDestroy(): void {
    this.dragCleanup.forEach((fn) => fn());
  }

  get outerTabBarStyles(): [string, string][] {
    return this.buttonType() === 'tab' ? [['border-bottom', 'tab-bar']] : [];
  }

  get scrollStyles(): [string, string][] {
    return [['gap', 'tab-bar']];
  }

  get underlineStyle(): string {
    return this.themeService.tabConfig().style;
  }

  get showBalanceInTab(): boolean {
    return this.themeService.tabConfig().showBalance;
  }

  onTabClick(index: number): void {
    if (this.hasDragged) {
      return;
    }
    this.activeTabIndex.set(index);
    this.tabSelected.emit(this.tabs()[index].id);
    this.updateUnderline();
    this.scrollToActiveTab();
  }

  private initDragScroll(): void {
    const el: HTMLElement | null = this.elementRef.nativeElement.querySelector('.tab-bar__scroll');
    if (!el) {
      return;
    }

    const onMouseDown = (e: MouseEvent) => {
      this.isDragging = true;
      this.hasDragged = false;
      this.dragStartX = e.pageX - el.offsetLeft;
      this.scrollStartLeft = el.scrollLeft;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isDragging) {
        return;
      }
      e.preventDefault();
      const delta = e.pageX - el.offsetLeft - this.dragStartX;
      if (Math.abs(delta) > 4) {
        this.hasDragged = true;
      }
      el.scrollLeft = this.scrollStartLeft - delta;
    };

    const onMouseUp = () => {
      this.isDragging = false;
      setTimeout(() => (this.hasDragged = false));
    };

    const onTouchStart = () => {
      this.isDragging = false;
      this.hasDragged = false;
    };

    el.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    el.addEventListener('touchstart', onTouchStart, { passive: true });

    this.dragCleanup = [
      () => el.removeEventListener('mousedown', onMouseDown),
      () => document.removeEventListener('mousemove', onMouseMove),
      () => document.removeEventListener('mouseup', onMouseUp),
      () => el.removeEventListener('touchstart', onTouchStart),
    ];
  }

  private scrollToActiveTab(behavior: ScrollBehavior = 'smooth'): void {
    const tabElements = Array.from(
      this.elementRef.nativeElement.querySelectorAll('app-pill-tab-button, app-tab-button'),
    ) as HTMLElement[];
    tabElements[this.activeTabIndex()]?.scrollIntoView({
      behavior,
      block: 'nearest',
      inline: 'nearest',
    });
  }

  private updateUnderline(): void {
    // Select both pill and tab button elements within this component only
    const tabElements = Array.from(
      this.elementRef.nativeElement.querySelectorAll('app-pill-tab-button, app-tab-button'),
    );

    if (tabElements.length > 0) {
      const selectedElement = tabElements[this.activeTabIndex()] as HTMLElement;
      if (selectedElement) {
        const { offsetLeft, offsetWidth } = selectedElement;
        this.underlineTransform =
          this.underlineStyle === 'circle'
            ? `translateX(${
                offsetLeft + offsetWidth / 2 - parseFloat(this.circleRadius) / 2
              }px) translateY(60%)`
            : `translateX(${offsetLeft}px)`;

        this.underlineWidth =
          this.underlineStyle === 'circle' ? this.circleRadius : `${offsetWidth}px`;

        this.changeDetection.markForCheck(); // Ensure change detection runs
      }
    }
  }
}
